import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faLocationDot, faMegaphone, faPaperPlane, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { RecipientEstimate } from '../audience';
import { CampaignDraft, EmailTemplate } from '../types';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { describeAudience, formatNumber } from '../utils';
import styles from './SummaryPanel.module.scss';

interface SummaryPanelProps {
    draft: CampaignDraft;
    estimate: RecipientEstimate;
    template: EmailTemplate | null;
}

// Sticky campaign summary shown next to the Locations and Audience steps.
export const SummaryPanel: React.FC<SummaryPanelProps> = ({ draft, estimate, template }) => {
    const { describeLocations } = useEmailMarketing();

    return (
        <section className={styles.root}>
            <h3 className={styles.title}>Campaign summary</h3>

            <div className={styles.recipients}>
                <span>Recipients</span>
                <strong>{estimate.known ? formatNumber(estimate.total) : '—'}</strong>
                <small>{describeAudience(draft.recipientType, draft.tags, draft)}</small>
            </div>

            <ul className={styles.list}>
                <li>
                    <FontAwesomeIcon icon={faMegaphone} />
                    <div>
                        <span>Campaign</span>
                        <strong>{draft.name || 'Untitled campaign'}</strong>
                    </div>
                </li>
                <li>
                    <FontAwesomeIcon icon={faLocationDot} />
                    <div>
                        <span>Locations</span>
                        <strong>{describeLocations(draft.locationIds)}</strong>
                    </div>
                </li>
                <li>
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <div>
                        <span>From</span>
                        <strong>Each location&apos;s verified email</strong>
                    </div>
                </li>
                {estimate.known && (
                    <li>
                        <FontAwesomeIcon icon={faUsers} />
                        <div>
                            <span>Shared customers</span>
                            <strong>{formatNumber(estimate.duplicates)} sent only once</strong>
                        </div>
                    </li>
                )}
                <li>
                    <FontAwesomeIcon icon={faFileLines} />
                    <div>
                        <span>Template</span>
                        <strong>{template?.name ?? 'Not selected yet'}</strong>
                    </div>
                </li>
            </ul>
        </section>
    );
};

export default SummaryPanel;
